import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import BetaBanner from '../components/common/betaBanner';
import Header from './header';
import { Sidebar } from './sidebar';

interface LayoutProps {
    children: React.ReactNode;
}

const SIDEBAR_EXPANDED_WIDTH = 324;
const SIDEBAR_CLOSE_DELAY = 150;

const Layout: React.FC<LayoutProps> = ({ children }) => {
    const location = useLocation();
    const isChatPage = location.pathname.startsWith('/chat');
    const isSettingsPage = location.pathname.startsWith('/settings');
    const isDealViewPage = location.pathname.startsWith('/deal/view');
    const isDashboardPage = location.pathname.startsWith('/dashboard');
    const [isBetaBannerClosed, setIsBetaBannerClosed] = useState(false);
    const showBetaBanner = isDashboardPage && !isBetaBannerClosed;
    const [isSidebarExpanded, setIsSidebarExpanded] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const expandedRef = useRef(false);
    const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const setExpanded = useCallback((value: boolean) => {
        expandedRef.current = value;
        setIsSidebarExpanded(value);
    }, []);

    const clearCloseTimer = useCallback(() => {
        if (closeTimerRef.current) {
            clearTimeout(closeTimerRef.current);
            closeTimerRef.current = null;
        }
    }, []);

    const scheduleClose = useCallback(() => {
        if (closeTimerRef.current) return;
        closeTimerRef.current = setTimeout(() => {
            closeTimerRef.current = null;
            setExpanded(false);
        }, SIDEBAR_CLOSE_DELAY);
    }, [setExpanded]);

    const handleSidebarEnter = () => {
        clearCloseTimer();
        setExpanded(true);
    };

    const handleCloseMobileMenu = useCallback(() => {
        setIsMobileMenuOpen(false);
    }, []);

    const handleOpenMobileMenu = useCallback(() => {
        setIsMobileMenuOpen(true);
    }, []);

    useEffect(() => {
        const handleMouseMove = (event: MouseEvent) => {
            if (!expandedRef.current) return;
            if (event.clientX <= SIDEBAR_EXPANDED_WIDTH) {
                clearCloseTimer();
            } else {
                scheduleClose();
            }
        };

        const handleWindowLeave = (event: MouseEvent) => {
            if (!event.relatedTarget) scheduleClose();
        };

        window.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseout', handleWindowLeave);

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseout', handleWindowLeave);
            clearCloseTimer();
        };
    }, [clearCloseTimer, scheduleClose]);

    return (
        <div className="flex h-screen flex-col overflow-hidden bg-gray-50">
            {showBetaBanner && <BetaBanner onClose={() => setIsBetaBannerClosed(true)} />}

            <div className="flex min-h-0 flex-1 overflow-hidden">
                <Sidebar
                    isExpanded={isSidebarExpanded}
                    isMobileOpen={isMobileMenuOpen}
                    hasTopBanner={showBetaBanner}
                    onMobileClose={handleCloseMobileMenu}
                    onDesktopMouseEnter={handleSidebarEnter}
                />

                <div
                    className={`fixed inset-0 z-30 bg-black/40 transition-opacity duration-300 lg:hidden ${
                        isMobileMenuOpen
                            ? 'opacity-100 pointer-events-auto'
                            : 'opacity-0 pointer-events-none'
                    }`}
                    onClick={handleCloseMobileMenu}
                />

                <div
                    className={`flex min-w-0 flex-1 flex-col h-full overflow-hidden transition-[margin] duration-300 ease-in-out ml-0 ${
                        isSidebarExpanded ? 'lg:ml-[324px]' : 'lg:ml-[80px]'
                    }`}
                >
                    <div className="sticky top-0 z-10 shrink-0 border-b border-gray-200 bg-white lg:hidden">
                        <Header onOpenMobileMenu={handleOpenMobileMenu} />
                    </div>

                    <main
                        data-app-scroll-container="true"
                        className={`flex-1 min-h-0 h-full bg-gray-50 ${isChatPage || isSettingsPage ? 'overflow-hidden' : 'overflow-y-auto'}`}
                    >
                        {children}
                    </main>
                </div>
            </div>
        </div>
    );
};

export default Layout;

